import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import ReactHtmlParser from 'react-html-parser'
import Section from '../common/Section'
import ConsentNotice from '../common/ConsentNotice'
import IframeResizer from 'iframe-resizer-react'

import useSSR from 'use-ssr'
import useConsent from '../../hooks/useConsent'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { get } from 'lodash'

// Extract iframe src
export function transform(node) {
  const { type, name } = node
  if (type === 'tag' && name === 'iframe') return node.attribs.src
}

// Extract script src/type from a string
function extractScriptAttributes(scriptString) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(scriptString, 'text/html')
  const scriptTag = doc.querySelector('script')
  if (!scriptTag) return null

  return {
    src: scriptTag.getAttribute('src'),
    type: scriptTag.getAttribute('type') || 'text/javascript',
  }
}

const EmbedModule = ({ primary: data }) => {
  const { isBrowser } = useSSR()
  const iframeRef = useRef()
  const jotformRef = useRef(null)
  const body = get(data, 'embed_body.html') || ''
  const bgColor = get(data, 'background_color') || 'transparent'
  const src = get(ReactHtmlParser(body, { transform }), '[0].props.children[0]')
  const [messageData, setMessageData] = useState()

  // Embedded iframes (forms, booking portals, videos) and arbitrary scripts are
  // third-party and can set cookies, so nothing in this module is rendered or
  // injected until the visitor consents (re-runs if consent changes).
  const embedConsent = useConsent('functional')
  const hasEmbed = body.includes('<iframe') || body.includes('<script')

  // JotForm is cross-origin, so iframe-resizer's contentWindow handshake can't
  // run inside it. Instead we listen for JotForm's own postMessage resize protocol.
  const isJotForm = /jotform/i.test(src || '')

  // Detect and inject script embed on mount (client only)
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!embedConsent) return
    if (body.includes('<script')) {
      const attrs = extractScriptAttributes(body)
      if (!attrs) return

      const script = document.createElement('script')
      script.src = attrs.src
      script.type = attrs.type
      script.async = true

      const target = document.getElementById('embed-script-container')
      if (target) {
        target.innerHTML = '' // Clear old content if any
        target.appendChild(script)
      }
    }
  }, [body, embedConsent])

  // Listen for JotForm's native resize messages and apply the reported height.
  useEffect(() => {
    if (typeof window === 'undefined' || !isJotForm) return undefined

    const handleMessage = event => {
      if (typeof event.data !== 'string') return
      if (!/jotform/i.test(event.origin)) return

      const [action, value] = event.data.split(':')
      const height = parseInt(value, 10)
      if (action === 'setHeight' && height && jotformRef.current) {
        jotformRef.current.style.height = `${height}px`
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isJotForm])

  // Listen for JotForm's native resize messages and apply the reported height.
  useEffect(() => {
    if (typeof window === 'undefined' || !isJotForm) return undefined

    const handleMessage = event => {
      if (typeof event.data !== 'string') return
      if (!/jotform/i.test(event.origin)) return

      const [action, value] = event.data.split(':')
      const height = parseInt(value, 10)
      if (action === 'setHeight' && height && jotformRef.current) {
        jotformRef.current.style.height = `${height}px`
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isJotForm])

  const onResized = data => {
    console.log('iframe resized', data)
    setMessageData(data)
  }

  const onMessage = data => {
    console.log('iframe message', data)
    setMessageData(data)
    iframeRef.current?.sendMessage('Hello back from the parent page')
  }

  return body ? (
    <Section id="embed-module" bgColor={bgColor} style={{ marginTop: '100px' }}>
      <Container>
        {!!isBrowser && hasEmbed && !embedConsent && (
          <Placeholder>
            <ConsentNotice message="This content is hidden until you accept cookies." />
          </Placeholder>
        )}
        {!!isBrowser && embedConsent && body.includes('<iframe') && isJotForm && (
          <iframe
            ref={jotformRef}
            src={src}
            title="JotForm form"
            scrolling="no"
            style={{
              width: '100%',
              minHeight: rem(640),
              border: 'none',
            }}
          />
        )}
        {!!isBrowser && embedConsent && body.includes('<iframe') && !isJotForm && (
          <IframeResizer
            forwardRef={iframeRef}
            autoResize
            sizeHeight
            heightCalculationMethod="lowestElement"
            minHeight={640}
            src={src}
            onMessage={onMessage}
            onResized={onResized}
            style={{
              width: '100%',
              overflow: 'visible',
            }}
          />
        )}
        {!!isBrowser && embedConsent && body.includes('<script') && (
          <div id="embed-script-container" />
        )}
      </Container>
    </Section>
  ) : null
}

EmbedModule.propTypes = {
  primary: PropTypes.object,
}

export default EmbedModule

const Container = styled.div`
  ${({ theme }) => css`
    position: relative;
    width: 100%;
    min-height: ${rem(140)};
    max-width: ${theme.layout.maxWidth};
    margin: 0;

    div {
      width: 100%;
      -webkit-overflow-scrolling: touch;
      overflow: scroll;

      > * {
        -webkit-overflow-scrolling: touch;
        overflow: scroll;
      }
    }

    > div,
    iframe {
      width: 100%;
    }

    #embed-script-container iframe {
      width: auto;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 0 auto;
    }
  `}
`

const Placeholder = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: ${rem(320)};
    padding: ${theme.space(4)};
    background-color: ${theme.color.blue};
    border-radius: ${theme.layout.borderRadius};
    text-align: center;
  `};
`
